import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';

import applicationRouter from '../../../hoc/applicationRouter.js';
import withLocalization from '../../../hoc/withLocalization.js';
import TwoFactorHistoryList from './TwoFactorHistoryList.js';

import Button from '../../../components/CustomButton/CustomButton.jsx';
import { Input } from '../../../elements/Input.js';
import OTPInput from '../../../elements/OtpInput.js';
import LoadingSpinner from '../../../elements/LoadingSpinner.js';
import CloseModalIcon from '../../../assets/img/CloseApproveModalIcon.svg';
import Select from '../../../elements/Select';
import { getTwoFactorReasonOptions } from '../../../utils/constants';
import { MICROSOFT_AUTHENTICATOR_ANDROID, MICROSOFT_AUTHENTICATOR_IOS, GOOGLE_AUTHENTICATOR_ANDROID, GOOGLE_AUTHENTICATOR_IOS } from '../../../library/config';

const isMobileDevice = () => /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

class TwoFactorSetup extends Component {

    constructor(props) {
        super(props);
        this.state = {
            is2FAEnabled: false,
            qrCode: null,
            secret: null,
            secretCopied: false,
            verificationCode: '',
            comment: '',
            reason: null,
            showQRCode: false,
            showDisableVerification: false,
            isVerifying: false,
            isLoadingQR: false,
            isLoading: true,
            history: { userId: null, userName: '' }
        };
    }

    componentDidMount() {
        this.check2FAStatus();
    }

    check2FAStatus = async () => {
        const { userStore } = this.props
        this.setState({ isLoading: true });
        try {
            const twoFactorSetting = await userStore.get2FAStatus();
            this.setState({ is2FAEnabled: twoFactorSetting.twoFactorEnabled });
        } catch (error) {
            console.error('Error checking 2FA status:', error);
        } finally {
            this.setState({ isLoading: false });
        }
    };

    handleSetup2FA = async () => {
        const { authStore } = this.props;
        this.setState({ isLoadingQR: true });
        try {
            const response = await authStore.setup2FA();
            this.setState({
                qrCode: response.qrCode,
                secret: response.secret,
                showQRCode: true
            });
        } catch (error) {
            console.error('Error setting up 2FA:', error);
        } finally {
            this.setState({ isLoadingQR: false });
        }
    };

    handleDisable2FA = () => {
        this.setState({ showDisableVerification: true });
    };

    handleDisableVerification = async () => {
        const { authStore, commonStore, t } = this.props;
        const { verificationCode, comment, reason } = this.state;

        if (!reason || (reason.value === t('Other') && !comment.trim())) {
            commonStore.addNotification(t('Please select a reason'), null, 'error');
            return;
        }

        if (!verificationCode) {
            commonStore.addNotification(t('Please enter the verification code'), null, 'error');
            return;
        }

        const resolvedComment = reason.value !== t('Other') ? reason.value : comment;

        this.setState({ isVerifying: true });

        try {
            const response = await authStore.disable2FA(verificationCode, resolvedComment);
            commonStore.addNotification(t(response.message), null, 'success')
            this.setState({
                is2FAEnabled: false,
                showQRCode: false,
                showDisableVerification: false,
                qrCode: null,
                verificationCode: '',
                comment: '',
                reason: null,
                isVerifying: false
            });
        } catch (error) {
            this.setState({ isVerifying: false });
        }
    };

    handleCopySecret = () => {
        const { secret } = this.state;
        navigator.clipboard.writeText(secret).then(() => {
            this.setState({ secretCopied: true });
            setTimeout(() => this.setState({ secretCopied: false }), 2000);
        });
    };

    handleCancelDisable = () => {
        this.setState({
            showDisableVerification: false,
            verificationCode: '',
            comment: '',
            reason: null,
        });
    };

    handleEnable2FA = async () => {
        const { authStore, source, commonStore, t } = this.props;

        const { verificationCode } = this.state;
        if (!verificationCode) return;

        this.setState({ isVerifying: true });

        try {
            const response = await authStore.enable2FA(verificationCode, source);
            if (response.twoFactorEnabled) {
                commonStore.addNotification(t(response.message), null, 'success')
                window.sessionStorage.removeItem('x-setup-2fa-token');

                this.setState({
                    is2FAEnabled: true,
                    showQRCode: false,
                    verificationCode: '',
                    isVerifying: false
                });
            }
        } catch (error) {
            this.setState({ isVerifying: false });
        }
    };

    handleSkip2FASetup = async () => {
        const { authStore } = this.props;

        try {
            await authStore.loginWithSkip2FASetup(true);
            window.sessionStorage.removeItem('x-setup-2fa-token');

            const lastPage = sessionStorage.getItem('lastPage');
            if (lastPage) {
                sessionStorage.removeItem('lastPage');
                this.props.router.navigate(lastPage);
            } else {
                this.props.router.navigate('/admin');
            }

        } catch (error) {
            console.error(error);
        }
    };

    render() {
        const { is2FAEnabled, qrCode, secret, secretCopied, showQRCode, verificationCode,
            isVerifying, isLoading, isLoadingQR, showDisableVerification, comment, reason, history } = this.state;

        const { t, source, userStore } = this.props;

        const disableReasonOptions = getTwoFactorReasonOptions(t);
        const userId = userStore.currentUser?.id;
        const userName = `${userStore.currentUser?.first_name} ${userStore.currentUser?.last_name}`;
        const userType = userStore.currentUser?.user_type;

        const ua = navigator.userAgent;
        const isIOS = /iphone|ipad|ipod/i.test(ua);
        const microsoftAuthLink = isIOS ? MICROSOFT_AUTHENTICATOR_IOS : MICROSOFT_AUTHENTICATOR_ANDROID;
        const googleAuthLink = isIOS ? GOOGLE_AUTHENTICATOR_IOS : GOOGLE_AUTHENTICATOR_ANDROID;

        if (isLoading) return <LoadingSpinner />;

        return (
            <div>
                {source !== 'login' && <div>
                    <div className='d-flex align-items-center justify-content-between gap-2 mb-3'>
                        <div className='fs18 fw-bold'>{t('Two-Factor Authentication')} {t('(2FA)')}</div>
                        {(userType === 'admin' || userType === 'super-admin') &&
                            <div>
                                <OverlayTrigger
                                    placement="top"
                                    overlay={<Tooltip id="tooltip-history">{t('View')} {t('2FA')} {t('History')}</Tooltip>}
                                >
                                    <Button
                                        icon_sm fill
                                        onClick={() => this.setState({ history: { userId, userName } })}
                                    >
                                        <i className="fa fa-history" />
                                    </Button>
                                </OverlayTrigger>
                            </div>
                        }
                    </div>
                </div>}

                {!is2FAEnabled && <div className="mb-3">
                    {t('Add an extra layer of security to your account by enabling two-factor authentication.')}
                </div>}

                {!is2FAEnabled ? (
                    <div>
                        {showQRCode ? (
                            <div style={{ maxWidth: '430px' }}>
                                <div className="text-center mb-2">
                                    <div className='fw-bold mb-2'>{t("Scan the QR code with your authenticator app")}</div>
                                    <img
                                        src={qrCode}
                                        alt="QR Code"
                                        style={{ maxWidth: '140px', width: '100%' }}
                                    />
                                    {secret && (
                                        <div className="mt-2">
                                            <div className="fw-bold mb-2">{t("Or enter this setup code manually")}:</div>
                                            <div className="d-flex align-items-center justify-content-center gap-2">
                                                <code style={{ fontSize: '13px', wordBreak: 'break-all' }}>{secret}</code>
                                                <span style={{ cursor: 'pointer' }} onClick={this.handleCopySecret}>
                                                    <i className={`fa ${secretCopied ? 'fa-check text-success' : 'fa-copy'}`} />
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <label className="mb-1 fw-bold">{t('Verification Code')}<span className="red-star">*</span></label>
                                </div>
                                <div className="twofa-otp-layout">
                                    <OTPInput
                                        length={6}
                                        type="number"
                                        autoFocus
                                        onChange={(code) => this.setState({ verificationCode: code })}
                                        onSubmit={this.handleEnable2FA}
                                        t={t}
                                    />
                                    <Button
                                        fill dynamic_lg
                                        onClick={this.handleEnable2FA}
                                        disabled={verificationCode.length !== 6 || isVerifying}
                                    >
                                        {isVerifying ? `${t('Verifying')}...` : `${t('Enable')} ${t('2FA')}`}
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <Button
                                fill dynamic_lg
                                onClick={this.handleSetup2FA}
                                disabled={isLoadingQR}
                            >
                                {isLoadingQR ? `${t('Loading...')}` : t('Show QR Code')}
                            </Button>
                        )}
                    </div>
                ) : (
                    <div>
                        {showDisableVerification ? (
                            <div style={{ maxWidth: '430px' }}>
                                <div className='mb-2'>{t('Please enter reason and verification code to disable 2FA.')}</div>
                                <div>
                                    <label className="genericLabel mb-1 fw-bold">{t('Reason')}<span className="red-star">*</span></label>
                                    <Select
                                        options={disableReasonOptions}
                                        value={reason}
                                        onChange={(selected) => this.setState({ reason: selected, comment: '' })}
                                        placeholder={t('Select a reason')}
                                        isDisabled={isVerifying}
                                        required
                                    />
                                    {reason && reason.value === t('Other') && (
                                        <Input
                                            className="mt-2"
                                            placeholder={t('Enter a reason')}
                                            value={comment}
                                            onChange={(e) => this.setState({ comment: e.target.value })}
                                            disabled={isVerifying}
                                            maxLength={255}
                                        />
                                    )}
                                </div>
                                <div className='mt-3'>
                                    <label className="genericLabel mb-1 fw-bold">{t('Verification Code')}<span className="red-star">*</span></label>
                                    <div className="twofa-otp-layout twofa-otp-layout--left">
                                        <OTPInput
                                            length={6}
                                            type="number"
                                            autoFocus
                                            onChange={(code) => this.setState({ verificationCode: code })}
                                            onSubmit={this.handleDisableVerification}
                                            t={t}
                                        />
                                        <div className="twofa-disable-buttons d-flex gap-2 mt-2">
                                            <Button
                                                fill dynamic_lg
                                                onClick={this.handleDisableVerification}
                                                disabled={verificationCode.length !== 6 || isVerifying}
                                            >
                                                {isVerifying ? `${t('Disabling')}...` : `${t('Disable')} ${t('2FA')}`}
                                            </Button>
                                            <Button
                                                className="btn btn-outline-secondary"
                                                onClick={this.handleCancelDisable}
                                                disabled={isVerifying}
                                            >
                                                {t('Cancel')}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div>
                                <div className="mb-3 fw-bold">
                                    {t('Two-Factor Authentication is enabled for your account.')}
                                </div>
                                {isMobileDevice() && (
                                    <p className="text-muted">{t('You can close this tab and login to mobile app or login to web')}</p>
                                )}
                                {
                                    source === 'settings' && (
                                        <Button
                                            onClick={this.handleDisable2FA}
                                        >
                                            {t('Disable')}{'/'}{t('Reset')} {t('2FA')}
                                        </Button>
                                    )}
                            </div>
                        )}
                    </div>
                )}

                {
                    !is2FAEnabled &&
                    <div className="mt-3">
                        <h5 className='fw-bold'>{t('How to set up 2FA:')}</h5>
                        <ol>
                            <li>{t('Install an authenticator app like')} <a href={microsoftAuthLink} target="_blank" rel="noreferrer">{t('Microsoft Authenticator')}</a> {t('or')} <a href={googleAuthLink} target="_blank" rel="noreferrer">{t('Google Authenticator')}</a> {t('on your phone')}.</li>
                            <li>{t('Open the app and scan the QR code, or enter the setup code manually.')}</li>
                            <li>{t('Enter the verification code from the app to complete the setup.')}</li>
                        </ol>
                    </div>
                }

                {
                    source === 'login' &&
                    <div className="d-flex w-100 justify-content-between mt-3 mb-3">
                        <button
                            className="fs18 loginFooter border-none"
                            onClick={() => this.props.router.navigate('/login')}
                        >
                            {t('Back to login')}
                        </button>
                        {
                            !is2FAEnabled &&
                            this.props.router.location?.state?.canSkip2FASetup &&
                            <button
                                className="fs18 loginFooter border-none"
                                onClick={this.handleSkip2FASetup}
                            >
                                {t('Skip 2FA Setup')}
                            </button>
                        }
                    </div>}

                <Modal
                    className='right modal_revised'
                    size="lg"
                    show={!!history.userId}
                    onHide={() => this.setState({ history: { userId: null, userName: '' } })}
                >
                    <Modal.Header>
                        <Modal.Title className="w-100 text-center m-0">
                            {t('2FA')} {t('History')} — {history.userName}
                        </Modal.Title>
                        <img
                            src={CloseModalIcon}
                            className="cursor-pointer"
                            alt="close"
                            onClick={() => this.setState({ history: { userId: null, userName: '' } })}
                        />
                    </Modal.Header>
                    <Modal.Body >
                        {history.userId && <TwoFactorHistoryList userId={history.userId} />}
                    </Modal.Body>
                </Modal>
            </div>
        );
    }
}

export default inject('authStore', 'userStore', 'commonStore')(applicationRouter(withLocalization(observer(TwoFactorSetup))));
